/**
 * Case owner, Hand off and "Log a call or message" (issue #201, Clayton: "i dont even see a place to set an owner or
 * worker on the ticket").
 *
 * - The header chip shows the owner as Desk has it (the primary ticket's assignee); a writer can change it, which
 *   writes Desk's assignee under their own Desk login (read back before Patchbay shows it).
 * - "Hand off…": new owner, what was passed on (a private note that @mentions them, so Desk and the Patchbay bell
 *   both notify), and the next move with its due time, in one form.
 * - "Log a call or message…": a softphone/phone/Teams/SMS contact kept as a private staff note in the timeline.
 *
 * A failed owner read says "unreachable", never "unassigned". Owner: feat/case-owner-handoff.
 */
import { Button, Chip, Dialog, Input, Picker, Textarea, toast } from '@patchbay/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { UserRound } from 'lucide-react'
import * as React from 'react'
import { ApiError } from '../board/api'
import { dueForPreset, toDateTimeLocal, toIso } from '../queue/date-presets'
import {
  CHANNELS,
  caseOwner,
  type DeskOwnerDto,
  handOff,
  logOffChannel,
  newKey,
  type OffChannel,
  type OwnerPersonDto,
  ownerHandoffSuggestion,
  ownerKeys,
  owners,
  rejectOwnerHandoff,
  setOwner,
} from './api'

const SELECT_CLASS = 'h-8 w-full rounded-md border border-input bg-card px-2 text-sm text-foreground'

function reason(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'connect_desk' || e.code === 'pending_token') return 'Connect Desk first: your own Desk login is used.'
    if (e.code === 'role') return 'Your role cannot write to Desk.'
    return e.message || e.code || 'Request failed'
  }
  return e instanceof Error ? e.message : String(e)
}

export function ownerLabel(owner: DeskOwnerDto | null | undefined): string {
  if (!owner) return 'No Desk ticket'
  if (!owner.desk_agent_id) return 'Unassigned in Desk'
  return owner.name || 'Desk agent'
}

function useCaseOwner(caseId: string) {
  return useQuery({ queryKey: ownerKeys.case(caseId), queryFn: () => caseOwner(caseId), retry: 1, retryDelay: 300 })
}

function useOwners(enabled: boolean) {
  return useQuery({ queryKey: ownerKeys.people, queryFn: owners, enabled, staleTime: 60_000 })
}

function useRefresh(caseId: string) {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ownerKeys.case(caseId) })
    void qc.invalidateQueries({ queryKey: ['case'] })
  }
}

export function OwnerSelect({
  value,
  onChange,
  people,
  unreachable,
  label = 'Owner',
}: {
  value: string
  onChange: (value: string) => void
  people: OwnerPersonDto[] | undefined
  unreachable: boolean
  label?: string
}) {
  return (
    <div className="block space-y-1 text-xs font-medium">
      {label}
      <Picker aria-label={label} className={SELECT_CLASS} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{people ? 'Pick a person' : 'Loading staff…'}</option>
        {(people ?? []).map((p) => (
          <option key={p.desk_agent_id} value={p.desk_agent_id}>
            {p.name}
            {p.user_id ? '' : ' (Desk only)'}
          </option>
        ))}
      </Picker>
      {unreachable ? (
        <span className="block font-normal text-warning">Desk's staff list is unreachable; only Patchbay users are listed.</span>
      ) : null}
    </div>
  )
}

/** Header chip: "Owner: Nick Lacey". A writer clicks it to change the owner. */
export function CaseOwnerChip({ caseId, canWrite }: { caseId: string; canWrite: boolean }) {
  const q = useCaseOwner(caseId)
  const [open, setOpen] = React.useState(false)
  if (q.isPending) return null
  if (q.isError) {
    return (
      <Chip className="min-w-0 shrink" tone="warning" title="The owner could not be read; it is unknown, not unassigned">
        <UserRound aria-hidden /> <span className="truncate">Owner unreachable</span>
      </Chip>
    )
  }
  const text = `Owner: ${ownerLabel(q.data)}`
  if (!canWrite || !q.data) {
    return (
      <Chip className="min-w-0 shrink" tone="neutral" data-testid="case-owner-chip" title="The Desk ticket's owner">
        <UserRound aria-hidden /> <span className="truncate">{text}</span>
      </Chip>
    )
  }
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="min-w-0 shrink"
        data-testid="case-owner-chip"
        title="Change the owner (writes Desk's assignee)"
        onClick={() => setOpen(true)}
      >
        <UserRound aria-hidden /> <span className="truncate">{text}</span>
      </Button>
      <SetOwnerDialog caseId={caseId} current={q.data} open={open} onClose={() => setOpen(false)} />
    </>
  )
}

export function SetOwnerDialog({
  caseId,
  current,
  open,
  onClose,
}: {
  caseId: string
  current?: DeskOwnerDto | null
  open: boolean
  onClose: () => void
}) {
  const people = useOwners(open)
  const refresh = useRefresh(caseId)
  const [agent, setAgent] = React.useState(current?.desk_agent_id ?? '')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  React.useEffect(() => {
    if (open) {
      setAgent(current?.desk_agent_id ?? '')
      setError(null)
    }
  }, [open, current?.desk_agent_id])
  const save = async () => {
    if (!agent) return
    setBusy(true)
    setError(null)
    try {
      const out = await setOwner(caseId, agent)
      toast(out.changed ? `Owner is now ${ownerLabel(out.owner)} in Desk` : 'Desk already had that owner')
      refresh()
      onClose()
    } catch (e) {
      setError(`Not changed: ${reason(e)}`)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => (o ? undefined : onClose())}
      title="Case owner"
      description="Sets the Desk ticket's owner under your own Desk login. Managers keep oversight in Patchbay either way."
    >
      <div className="space-y-3">
        <OwnerSelect
          value={agent}
          onChange={setAgent}
          people={people.data?.people}
          unreachable={people.data?.desk_directory === 'unreachable'}
        />
        {people.isError ? <p className="text-sm text-warning">Staff could not be listed: {reason(people.error)}</p> : null}
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!agent || busy}>
            {busy ? 'Saving…' : 'Set owner'}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

function firstName(name: string | undefined): string {
  return (name ?? '').trim().split(/\s+/)[0] ?? ''
}

export function HandOffDialog({
  caseId,
  open,
  onClose,
  context,
}: {
  caseId: string
  open: boolean
  onClose: () => void
  context?: string
}) {
  const people = useOwners(open)
  const suggestion = useQuery({
    queryKey: ownerKeys.suggestion(caseId),
    queryFn: () => ownerHandoffSuggestion(caseId),
    enabled: open,
    retry: false,
  })
  const refresh = useRefresh(caseId)
  const [agent, setAgent] = React.useState('')
  const [channel, setChannel] = React.useState<OffChannel>('softphone')
  const [passedOn, setPassedOn] = React.useState('')
  const [move, setMove] = React.useState('')
  const [moveTouched, setMoveTouched] = React.useState(false)
  const [due, setDue] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [rejectedSuggestion, setRejectedSuggestion] = React.useState<string | null>(null)
  const appliedSuggestion = React.useRef<string | null>(null)
  const key = React.useRef(newKey())
  React.useEffect(() => {
    if (!open) return
    setAgent('')
    setChannel('softphone')
    setPassedOn(context?.trim() ?? '')
    setMove('')
    setMoveTouched(false)
    setDue(toDateTimeLocal(new Date(Date.now() + 60 * 60 * 1000)))
    setError(null)
    setRejectedSuggestion(null)
    appliedSuggestion.current = null
    key.current = newKey()
  }, [open, context])
  React.useEffect(() => {
    const row = suggestion.data
    if (!open || !row?.suggestion_id || !row.owner || row.suggestion_id === appliedSuggestion.current) return
    appliedSuggestion.current = row.suggestion_id
    setAgent(row.owner.desk_agent_id)
    if (!moveTouched) setMove(`${firstName(row.owner.name)} reviews the case and contacts the customer`)
  }, [open, suggestion.data, moveTouched])
  const pick = (value: string) => {
    setAgent(value)
    const person = people.data?.people.find((p) => p.desk_agent_id === value)
    if (!moveTouched && person) setMove(`${firstName(person.name)} calls the customer`)
  }
  const dueIso = toIso(due)
  const ready = Boolean(agent && passedOn.trim() && move.trim() && dueIso)
  const submit = async () => {
    if (!ready || !dueIso) return
    setBusy(true)
    setError(null)
    try {
      const out = await handOff(caseId, {
        desk_agent_id: agent,
        channel,
        passed_on: passedOn.trim(),
        next_move_text: move.trim(),
        due_at: dueIso,
        idempotency_key: key.current,
        ...(suggestion.data?.suggestion_id && suggestion.data.suggestion_id !== rejectedSuggestion
          ? { suggestion_id: suggestion.data.suggestion_id }
          : {}),
      })
      refresh()
      if (!out.note.posted) {
        // The owner and next move are set; only the note is missing. Say so, and let the person retry it.
        setError(`Owner and next move are set, but the handoff note did not post: ${out.note.detail ?? out.note.code ?? 'unknown'}`)
        return
      }
      toast(`Handed to ${ownerLabel(out.owner)}`)
      onClose()
    } catch (e) {
      setError(`Not handed off: ${reason(e)}`)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => (o ? undefined : onClose())}
      title="Hand off"
      description="Makes them the Desk owner, sets the next move, and leaves a private note that @mentions them."
    >
      <div className="space-y-3">
        {suggestion.data?.suggestion_id && suggestion.data.owner && suggestion.data.suggestion_id !== rejectedSuggestion ? (
          <section className="rounded-md border border-primary/30 bg-primary-soft px-3 py-2" aria-label="Riley owner suggestion">
            <p className="text-sm font-semibold">Riley suggests {suggestion.data.owner.name}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{suggestion.data.why}</p>
            {suggestion.data.category ? <p className="mt-1 text-xs">Category: {suggestion.data.category}</p> : null}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="mt-1"
              onClick={async () => {
                const id = suggestion.data?.suggestion_id
                if (!id) return
                try {
                  await rejectOwnerHandoff(caseId, id)
                  setRejectedSuggestion(id)
                  setAgent('')
                  setMove('')
                  toast('Owner suggestion rejected')
                } catch (e) {
                  setError(`Could not reject suggestion: ${reason(e)}`)
                }
              }}
            >
              Not this suggestion
            </Button>
          </section>
        ) : suggestion.data ? (
          <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">{suggestion.data.why}</p>
        ) : suggestion.isError ? (
          <p className="text-xs text-warning">Owner evidence is unreachable; you can still hand off manually.</p>
        ) : null}
        <OwnerSelect
          label="Hand to"
          value={agent}
          onChange={pick}
          people={people.data?.people}
          unreachable={people.data?.desk_directory === 'unreachable'}
        />
        {/* biome-ignore lint/a11y/noLabelWithoutControl: Picker renders the nested combobox input. */}
        <label className="block space-y-1 text-xs font-medium">
          Passed on by
          <Picker
            aria-label="Passed on by"
            className={SELECT_CLASS}
            value={channel}
            onChange={(e) => setChannel(e.target.value as OffChannel)}
          >
            {CHANNELS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Picker>
        </label>
        <label className="block space-y-1 text-xs font-medium">
          What you passed on (private note)
          <Textarea aria-label="What you passed on" value={passedOn} onChange={(e) => setPassedOn(e.target.value)} maxLength={4000} />
        </label>
        <label className="block space-y-1 text-xs font-medium">
          Next move
          <Input
            aria-label="Next move"
            value={move}
            maxLength={500}
            onChange={(e) => {
              setMove(e.target.value)
              setMoveTouched(true)
            }}
          />
        </label>
        <fieldset className="space-y-1.5">
          <legend className="text-xs font-medium">Due</legend>
          <div className="flex flex-wrap gap-1">
            <Button type="button" size="sm" onClick={() => setDue(toDateTimeLocal(new Date(Date.now() + 60 * 60 * 1000)))}>
              In 1 hour
            </Button>
            <Button type="button" size="sm" onClick={() => setDue(toDateTimeLocal(dueForPreset('today')))}>
              Today
            </Button>
            <Button type="button" size="sm" onClick={() => setDue(toDateTimeLocal(dueForPreset('tomorrow')))}>
              Tomorrow
            </Button>
          </div>
          <Input aria-label="Due" type="datetime-local" required value={due} onChange={(e) => setDue(e.target.value)} />
        </fieldset>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!ready || busy}>
            {busy ? 'Handing off…' : 'Hand off'}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

export function OffChannelDialog({ caseId, open, onClose }: { caseId: string; open: boolean; onClose: () => void }) {
  const refresh = useRefresh(caseId)
  const [channel, setChannel] = React.useState<OffChannel>('phone')
  const [who, setWho] = React.useState('')
  const [summary, setSummary] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const key = React.useRef(newKey())
  React.useEffect(() => {
    if (!open) return
    setChannel('phone')
    setWho('')
    setSummary('')
    setError(null)
    key.current = newKey()
  }, [open])
  const ready = Boolean(who.trim() && summary.trim())
  const submit = async () => {
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      await logOffChannel(caseId, { channel, with_whom: who.trim(), summary: summary.trim(), idempotency_key: key.current })
      toast('Logged as a private staff note')
      refresh()
      onClose()
    } catch (e) {
      setError(`Not logged: ${reason(e)}`)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => (o ? undefined : onClose())}
      title="Log a call or message"
      description="Something that happened outside Desk. It is kept as a private staff note on the ticket."
    >
      <div className="space-y-3">
        {/* biome-ignore lint/a11y/noLabelWithoutControl: Picker renders the nested combobox input. */}
        <label className="block space-y-1 text-xs font-medium">
          Channel
          <Picker aria-label="Channel" className={SELECT_CLASS} value={channel} onChange={(e) => setChannel(e.target.value as OffChannel)}>
            {CHANNELS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Picker>
        </label>
        <label className="block space-y-1 text-xs font-medium">
          With
          <Input aria-label="With" placeholder="Jane at reception" value={who} maxLength={200} onChange={(e) => setWho(e.target.value)} />
        </label>
        <label className="block space-y-1 text-xs font-medium">
          What was said
          <Textarea aria-label="What was said" value={summary} maxLength={4000} onChange={(e) => setSummary(e.target.value)} />
        </label>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!ready || busy}>
            {busy ? 'Logging…' : 'Log it'}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
